import { useMutation, useQuery } from "@apollo/client";
import { useWorker } from "@koale/useworker";
import gql from "graphql-tag";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { animated, config, useTrail } from "react-spring";
import { toast } from "react-toastify";
import { toastErrorOptions } from "../../../../App";
import { handleGraphQLErrors } from "../../../../utils/handleGraphQLError";
import { ListLoading } from "../../../SharedComponents/ListLoading/ListLoading";
import { Modal } from "../../../SharedComponents/Modal/Modal";
import "./AdminCountriesList.css";

export const AdminCountriesList = ({ searchTerm }) => {
  const { t } = useTranslation();
  const { data, error, loading } = useQuery(countriesQuery, {
    fetchPolicy: "network-only",
  });

  const [filterWorker, { kill }] = useWorker(filterCountriesFunction);
  const [filteredCountries, setFilteredCountries] = useState([]);

  useEffect(
    () => {
      if (!data?.countries) return;
      if (searchTerm === "") {
        setFilteredCountries(data?.countries);
      }
      kill();

      filterWorker(data.countries, searchTerm)
        .then(setFilteredCountries)
        .catch(() => {
          setFilteredCountries(data?.countries);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, searchTerm]
  );

  if (loading) return <ListLoading />;

  if (error) {
    toast(t("errors.countries"), toastErrorOptions);
  }

  return (
    <div className="AdminCountriesList">
      <div className="AdminCountriesList__titlebar">
        <div className="AdminCountriesList__titlebar-name">
          {t("generic.name")}
        </div>
        <div className="AdminCountriesList__titlebar-code">
          {t("generic.code")}
        </div>
      </div>
      {filteredCountries.map((country) => (
        <AdminCountriesListItem country={country} key={country?.name} />
      ))}
    </div>
  );
};

const filterCountriesFunction = (countries, searchTerm) => {
  return countries.filter((x) => x?.name?.toLowerCase()?.includes(searchTerm));
};

const countriesQuery = gql`
  query countries {
    countries {
      id
      name
      code
    }
  }
`;

const AdminCountriesListItem = ({ country }) => {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCountry] = useMutation(deleteCountryMutation, {
    variables: { id: country?.id },
    update: (cache, result) => {
      const prev = cache.readQuery({ query: countriesQuery });

      const filteredCountries = prev?.countries?.filter(
        (x) => x.id !== result.data.deleteCountry
      );

      const newCountries = {
        ...prev,
        countries: [...filteredCountries],
      };

      cache.writeQuery({ query: countriesQuery, data: newCountries });
    },
  });
  const history = useHistory();
  const listActions = [{ label: "Edit country" }, { label: "Delete country" }];
  const [mouseOver, setMouseOver] = useState(false);
  const trail = useTrail(listActions.length, {
    config: config.wobbly,
    opacity: mouseOver ? 1 : 0,
    from: { opacity: 0 },
  });

  const actionChooser = (actionLabel) => {
    switch (actionLabel) {
      case "Edit country":
        return history.push("/admin/administration/country/" + country.id);

      case "Delete country":
        setShowDeleteModal(true);
        break;

      default:
        return;
    }
  };

  return (
    <>
      {showDeleteModal && (
        <Modal
          title="Delete country"
          content={
            <div>
              This action will delete this country as an option. Any accounts
              using the country selected, will not be affected
            </div>
          }
          acceptText={t("generic.delete")}
          rejectText={t("generic.cancel")}
          reject={() => setShowDeleteModal(false)}
          accept={() => {
            deleteCountry()
              .then(() => {
                setShowDeleteModal(false);
              })
              .catch((err) => handleGraphQLErrors(err));
          }}
          acceptButtonColor="#AA2525"
        />
      )}
      <div
        className="AdminCountriesListItem"
        onMouseEnter={() => setMouseOver(true)}
        onMouseLeave={() => setMouseOver(false)}
      >
        <div className="AdminCountriesListItem__name">{country?.name}</div>
        <div className="AdminCountriesListItem__code">{country?.code}</div>
        <div className="AdminCountriesListItem__actions">
          {mouseOver &&
            trail.map((props, index) => (
              <animated.div
                className="AdminCountriesListItem__action"
                key={index}
                onClick={() => actionChooser(listActions[index].label)}
              >
                <div className="AdminCountriesListItem__action-text">
                  {listActions[index].label}
                </div>
              </animated.div>
            ))}
        </div>
      </div>
    </>
  );
};

const deleteCountryMutation = gql`
  mutation deleteCountry($id: String) {
    deleteCountry(id: $id)
  }
`;
